import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ApiService, { llmBillingApi, webhooksApi } from '../services/api';

const INTEGRATIONS_QUERY_KEY = ['integrations'];
// GitLab multi-instance list query key — declared up here (alongside the
// aggregate key) so both the connect + disconnect hooks below can invalidate
// it without a forward reference.
const GITLAB_INTEGRATIONS_QUERY_KEY = ['gitlab-integrations'];

// Provider IDs whose Connect/Disconnect/OAuth-start routes live on the
// SEPARATE llm-billing RestApi (LlmBillingRoutesNestedStack), not the
// main api-prod RestApi. Status reads are still served by the main
// api-prod RestApi via /integrations/status (aggregated), so this list
// only matters for write mutations + OAuth start.
const LLM_BILLING_PROVIDERS = new Set([
  'openai_billing',
  'anthropic_billing',
  'notion',
  // Plane connect/status/disconnect routes also live on the llm-billing
  // RestApi (LlmBillingRoutesNestedStack). Status is still aggregated via
  // the main /integrations/status endpoint; this only routes mutations.
  'plane',
  // Linear connect/status/disconnect routes also live on the llm-billing
  // RestApi (LlmBillingRoutesNestedStack). Same as Plane — status is
  // aggregated via /integrations/status; this only routes mutations.
  'linear',
  // Figma OAuth start/callback/disconnect routes live on the llm-billing
  // RestApi (LlmBillingRoutesNestedStack), same as Notion. Status is still
  // aggregated via the main /integrations/status endpoint; this list routes
  // the OAuth start + disconnect to the right RestApi.
  'figma',
  // HubSpot OAuth start/callback/disconnect routes live on the llm-billing
  // RestApi (LlmBillingRoutesNestedStack), same as Notion/Figma. Status is
  // aggregated via the main /integrations/status endpoint; this list routes
  // the OAuth start + disconnect to the right RestApi.
  'hubspot',
  // CircleCI connect/status/disconnect routes also live on the llm-billing
  // RestApi (LlmBillingRoutesNestedStack). Same as Linear/Plane — status is
  // aggregated via /integrations/status; this only routes mutations.
  'circleci',
  // Discord connect/disconnect routes also live on the llm-billing RestApi
  // (LlmBillingRoutesNestedStack). Same as CircleCI — status is aggregated
  // via /integrations/status; this only routes mutations.
  'discord',
  // Discord PER-USER identity binding (OAuth `identify`) — the self-serve card
  // that links the CALLER's own Discord account to their Zibby account. Routes
  // (/discord/user/oauth/start, /discord/user/disconnect) live on the same
  // llm-billing RestApi. Status is aggregated via /integrations/status as
  // `discord_user` (the caller's own binding). SEPARATE from the account-level
  // `discord` server connect above.
  'discord_user',
  // LinkedIn OAuth start/callback/disconnect routes live on the llm-billing
  // RestApi (LlmBillingRoutesNestedStack), same as Notion/Figma. Two variants
  // (personal member posting + business org pages) share the /linkedin/* routes
  // disambiguated by ?variant=. 'linkedin' routes the OAuth start; the two
  // variant ids route their disconnects. Status is aggregated via
  // /integrations/status.
  'linkedin',
  'linkedin_personal',
  'linkedin_business',
  // Google (Docs/Drive) OAuth start/callback/disconnect routes live on the
  // llm-billing RestApi (LlmBillingRoutesNestedStack), same as Notion.
  // Status is aggregated via /integrations/status; this list routes the
  // OAuth start + disconnect to the right RestApi. NOTE: this is the
  // Docs/Drive INTEGRATION (provider 'google'), not Google sign-in.
  'google',
]);

const apiFor = (service) => (LLM_BILLING_PROVIDERS.has(service) ? llmBillingApi : ApiService);

export const useAllIntegrationStatuses = () => {
  return useQuery({
    queryKey: INTEGRATIONS_QUERY_KEY,
    queryFn: () => ApiService.request('/integrations/status'),
  });
};

export const useInvalidateIntegrations = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
};

// Burst refetch — refetch /integrations/status NOW, then again shortly after.
// Used when an OAuth popup signals completion: the immediate refetch makes the
// card flip instantly, and the trailing refetches cover the (rare) race where
// the popup's postMessage lands a beat before the backend callback's write is
// readable. Fire-and-forget: invalidateQueries on the app-level queryClient is
// safe even if the page unmounts before a timer fires.
const BURST_DELAYS_MS = [0, 2000, 5000];
export const useRefetchIntegrationsBurst = () => {
  const queryClient = useQueryClient();
  return () => {
    BURST_DELAYS_MS.forEach((ms) => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
      }, ms);
    });
  };
};

// Map a service identifier to its REST path prefix. Most providers use
// `/<service>/disconnect`, but the llm-billing providers use hyphen-separated
// prefixes (`/openai-billing/...`) to match the backend handler's path
// dispatch table in handlers/llm-billing.js → PROVIDERS[].pathPrefix.
const DISCONNECT_PATH = {
  openai_billing: '/openai-billing/disconnect',
  anthropic_billing: '/anthropic-billing/disconnect',
  notion: '/notion/disconnect',
  plane: '/plane/disconnect',
  linear: '/linear/disconnect',
  figma: '/figma/disconnect',
  circleci: '/circleci/disconnect',
  discord: '/discord/disconnect',
  discord_user: '/discord/user/disconnect',
  linkedin_personal: '/linkedin/disconnect?variant=personal',
  linkedin_business: '/linkedin/disconnect?variant=business',
  google: '/google/disconnect',
};

export const useDisconnectIntegration = (service) => {
  const queryClient = useQueryClient();
  const client = apiFor(service);
  const path = DISCONNECT_PATH[service] || `/${service}/disconnect`;

  return useMutation({
    // GitLab is multi-instance: pass { integrationId } to disconnect ONE specific
    // instance (the backend validates ownership + clears any link pins to it).
    // Omit it (or call with no args) for the account-wide disconnect (back-compat).
    mutationFn: (vars) => client.request(path, {
      method: 'POST',
      ...(vars && vars.integrationId
        ? { body: JSON.stringify({ integrationId: vars.integrationId }) }
        : {}),
    }),
    // OPTIMISTIC disconnect — flip the card to "Connect" the moment the user
    // confirms, instead of leaving it "Connected" for the several seconds the
    // POST (possibly a cold Lambda) + status refetch round-trip takes. Snapshot
    // the caches first and roll back on failure; onSettled still revalidates
    // against the server so the optimistic state can never drift.
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
      const prevStatus = queryClient.getQueryData(INTEGRATIONS_QUERY_KEY);
      if (prevStatus?.[service]) {
        queryClient.setQueryData(INTEGRATIONS_QUERY_KEY, {
          ...prevStatus,
          [service]: { ...prevStatus[service], connected: false },
        });
      }
      // GitLab is multi-instance with its own list query — optimistically drop
      // the one instance being disconnected (or all, for the account-wide call)
      // so its card flips too.
      let prevGitlab;
      if (service === 'gitlab') {
        await queryClient.cancelQueries({ queryKey: GITLAB_INTEGRATIONS_QUERY_KEY });
        prevGitlab = queryClient.getQueryData(GITLAB_INTEGRATIONS_QUERY_KEY);
        if (prevGitlab?.integrations) {
          queryClient.setQueryData(GITLAB_INTEGRATIONS_QUERY_KEY, {
            ...prevGitlab,
            integrations: vars?.integrationId
              ? prevGitlab.integrations.filter((i) => i.integrationId !== vars.integrationId)
              : [],
          });
        }
      }
      return { prevStatus, prevGitlab };
    },
    onError: (_err, _vars, context) => {
      if (context?.prevStatus) {
        queryClient.setQueryData(INTEGRATIONS_QUERY_KEY, context.prevStatus);
      }
      if (service === 'gitlab' && context?.prevGitlab) {
        queryClient.setQueryData(GITLAB_INTEGRATIONS_QUERY_KEY, context.prevGitlab);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
      // GitLab has its own list — also refetch it so disconnecting one instance
      // flips its Integrations card back to "Connect".
      if (service === 'gitlab') {
        queryClient.invalidateQueries({ queryKey: GITLAB_INTEGRATIONS_QUERY_KEY });
      }
    },
  });
};

export const useConnectGitlab = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // signingToken is OPTIONAL — the per-webhook `whsec_…` GitLab generates so
    // inbound deliveries can be HMAC-validated per-tenant. The backend
    // (handlers/gitlab.js connectGitlab) accepts + stores it; omit it and the
    // legacy static-token path still works (and a reconnect preserves a prior
    // token). Only send it when non-empty so we never overwrite a stored token
    // with a blank.
    mutationFn: ({ instanceUrl, accessToken, signingToken }) => {
      const body = { instanceUrl, accessToken };
      if (signingToken && signingToken.trim()) body.signingToken = signingToken.trim();
      return ApiService.request('/gitlab/connect', {
        method: 'POST',
        body: JSON.stringify(body)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
      // Also refetch the multi-instance list so a newly-added instanceUrl
      // (which ADDS a row, never overwrites a different instanceUrl) shows up.
      queryClient.invalidateQueries({ queryKey: GITLAB_INTEGRATIONS_QUERY_KEY });
    },
  });
};

// ── GitLab INTEGRATIONS — GET /gitlab/integrations ───────────────────────────
// An account may hold a Cloud (gitlab.com) AND a Self-Hosted gitlab integration.
// /integrations/status collapses gitlab to one-per-provider and omits the
// integrationId, so it can't tell the two Integrations cards apart or carry the
// id each card needs to disconnect/pin its own instance. This endpoint returns
// EVERY gitlab integration with its integrationId + public-safe descriptor
// fields (NO tokens). It lives on the dedicated Webhooks RestApi (same stack as
// /gitlab/links), NOT the main api-prod RestApi — confirmed in
// backend/src/handlers/gitlab-links.js.
export const useGitlabIntegrations = () => {
  return useQuery({
    queryKey: GITLAB_INTEGRATIONS_QUERY_KEY,
    // → { integrations: [{ integrationId, instanceUrl, username, status, connectedAt }] }
    queryFn: () => webhooksApi.request('/gitlab/integrations'),
  });
};

export const useInvalidateGitlabIntegrations = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: GITLAB_INTEGRATIONS_QUERY_KEY });
};

// ── GitLab repo↔agent LINKS (the /gitlab/links CRUD, on the Webhooks RestApi) ─
// The GitLab analog of /github/links. A link maps a GitLab project/group/path
// to ONE deployed agent (workflow slug) that reviews its MRs. These power the
// repo→agent picker in GitlabConnectModal. They live on webhooksApi (the
// dedicated Webhooks RestApi), NOT the main ApiService.
const gitlabLinksKey = (projectId) => ['gitlab-links', projectId];

export const useGitlabLinks = (projectId) => {
  return useQuery({
    queryKey: gitlabLinksKey(projectId),
    queryFn: () => webhooksApi.request(`/gitlab/links?projectId=${encodeURIComponent(projectId)}`),
    enabled: !!projectId,
  });
};

export const useCreateGitlabLink = (projectId) => {
  const queryClient = useQueryClient();
  return useMutation({
    // body: { kind, identifier, projectId, agentSlug, commentResponseSlug? }
    //   kind ∈ { 'project', 'group', 'path' }
    mutationFn: (body) => webhooksApi.request('/gitlab/links', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gitlabLinksKey(projectId) });
    },
  });
};

export const useDeleteGitlabLink = (projectId) => {
  const queryClient = useQueryClient();
  return useMutation({
    // body: { kind, identifier }
    mutationFn: (body) => webhooksApi.request('/gitlab/links', {
      method: 'DELETE',
      body: JSON.stringify(body),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gitlabLinksKey(projectId) });
    },
  });
};

export const useConnectLark = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // overrideProjectId (optional) connects the app as ONE project's OWN Lark
    // app (per-project Copilot override) instead of the account default.
    mutationFn: ({ appId, appSecret, verificationToken, encryptKey, host, overrideProjectId }) => {
      return ApiService.request('/lark/connect', {
        method: 'POST',
        body: JSON.stringify({
          appId, appSecret, verificationToken, encryptKey, host,
          ...(overrideProjectId ? { overrideProjectId } : {}),
        })
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
    },
  });
};

// Shared factory for the three paste-token LLM-billing connectors. They
// all POST { adminKey } to /<provider>/connect on the LlmBilling RestApi
// and refresh the integrations cache on success. Backend validation
// returns a structured error (status 400/401/403 + message) that
// ApiClient.request preserves on err.message + err.status — callers
// surface err.message directly in the modal.
const makeConnectLlmBillingHook = (pathPrefix) => () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ adminKey }) => {
      return llmBillingApi.request(`${pathPrefix}/connect`, {
        method: 'POST',
        body: JSON.stringify({ adminKey }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
    },
  });
};

export const useConnectOpenAIBilling = makeConnectLlmBillingHook('/openai-billing');
export const useConnectAnthropicBilling = makeConnectLlmBillingHook('/anthropic-billing');

// Plane — api-key paste-token connector. Unlike the LLM-billing connectors it
// posts THREE values { apiKey, workspaceSlug, baseUrl } to /plane/connect
// on the same llm-billing RestApi. baseUrl is optional client-side
// (backend defaults to https://api.plane.so when blank).
export const useConnectPlane = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ apiKey, workspaceSlug, baseUrl }) => {
      return llmBillingApi.request('/plane/connect', {
        method: 'POST',
        body: JSON.stringify({ apiKey, workspaceSlug, baseUrl }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
    },
  });
};

// Linear — api-key paste-token connector. Posts a SINGLE value { apiKey }
// to /linear/connect on the same llm-billing RestApi. Simpler than Plane
// (no workspaceSlug/baseUrl). Backend validates the key (Linear viewer
// query) before persisting; structured errors surface on err.message.
export const useConnectLinear = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ apiKey }) => {
      return llmBillingApi.request('/linear/connect', {
        method: 'POST',
        body: JSON.stringify({ apiKey }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
    },
  });
};

// Figma — paste-token (personal access token) connector. Posts a SINGLE
// value { apiKey } to /figma/connect on the same llm-billing RestApi.
// Same shape as Linear (no workspaceSlug/baseUrl). Backend validates the
// PAT (Figma /v1/me with X-Figma-Token) before persisting; structured
// errors surface on err.message.
export const useConnectFigma = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ apiKey }) => {
      return llmBillingApi.request('/figma/connect', {
        method: 'POST',
        body: JSON.stringify({ apiKey }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
    },
  });
};

// CircleCI — api-key (personal API token) paste-token connector. Posts a
// SINGLE value { apiKey } to /circleci/connect on the same llm-billing
// RestApi. Same shape as Linear (no workspaceSlug/baseUrl). CircleCI has no
// consumer OAuth, so paste-token is the only flow. Backend validates the
// token (GET /api/v2/me with the Circle-Token header) before persisting;
// structured errors surface on err.message.
export const useConnectCircleCI = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ apiKey }) => {
      return llmBillingApi.request('/circleci/connect', {
        method: 'POST',
        body: JSON.stringify({ apiKey }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
    },
  });
};

// Discord — paste-token connector. Posts { guildId, botToken?, applicationId? }
// to /discord/connect on the same llm-billing RestApi. guildId is REQUIRED
// (the server the bot/Copilot operates in); botToken is OPTIONAL (needed for
// agents to post messages — the /zibby Copilot command works without it), so
// callers omit the key entirely when blank rather than sending an empty
// string. Backend validates (401 bad token, 400 bad guildId) before
// persisting; structured errors surface on err.message.
export const useConnectDiscord = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => {
      return llmBillingApi.request('/discord/connect', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
    },
  });
};

// Sentry — SELF-HOSTED connector (auth-token/PAT), the manual alternative to
// the SaaS OAuth "Connect" button. Posts THREE values
// { baseUrl, authToken, organizationSlug } to /sentry/connect. Unlike the
// llm-billing paste-token providers, Sentry's connect/status/disconnect routes
// live on the MAIN api-prod RestApi (ApiService) — same place as the Sentry
// OAuth start (`useStartOAuth('sentry')`) — NOT the llm-billing RestApi.
// Backend validates the token against the given base URL + org slug (the
// self-hosted default org slug is literally "sentry") before persisting;
// structured errors (400/401 + { error }) surface on err.message.
export const useConnectSentry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ baseUrl, authToken, organizationSlug }) => {
      return ApiService.request('/sentry/connect', {
        method: 'POST',
        body: JSON.stringify({ baseUrl, authToken, organizationSlug }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
    },
  });
};

// Vikunja — paste-token connector for a self-hosted Vikunja board. Posts TWO
// values { instanceUrl, token } to /vikunja/connect on the MAIN api-prod
// RestApi (same place as Sentry's self-hosted connect — NOT the llm-billing
// RestApi; the routes hang off `api.root.addResource('vikunja')` in
// lib/constructs/main-api-routes-stack.ts). Status is aggregated via
// /integrations/status and disconnect takes the default `/vikunja/disconnect`,
// so neither needs an entry in LLM_BILLING_PROVIDERS or DISCONNECT_PATH.
//
// Vikunja has no OAuth and no admin token-minting API, so the user mints the
// token themselves in their board's Settings → API Tokens. The backend
// validates it against the instance (GET /api/v1/projects) BEFORE persisting
// and returns the boards it found; structured errors (400 rejected token /
// bad URL, 502 unreachable) surface on err.message.
export const useConnectVikunja = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ instanceUrl, token }) => {
      return ApiService.request('/vikunja/connect', {
        method: 'POST',
        body: JSON.stringify({ instanceUrl, token }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
    },
  });
};

export const useStartOAuth = (service) => {
  const client = apiFor(service);
  return useMutation({
    // Pass `{ returnTo }` via mutate() to override where the OAuth
    // callback redirects the user after auth. The marketplace detail
    // modal uses this to point popup-OAuth back at
    // `/oauth-popup-done.html` so the popup can close itself and
    // postMessage the opener. Without `returnTo`, the backend's
    // callback falls back to `/integrations?<service>=success` (the
    // canonical path).
    //
    // Notion lives on the llm-billing RestApi (Agent A's stack); all
    // other OAuth services live on the main api-prod RestApi. The
    // client switch above routes the request automatically based on
    // the service id.
    // `variant` (LinkedIn personal|business) is appended as a query param —
    // both variants share the /linkedin/oauth/start route, disambiguated here.
    // `projectId` (optional) → a PER-PROJECT connect (Project Settings → Chat):
    // the backend binds the workspace to THAT project's Copilot. Mirrors the
    // Discord one-click server install. Account-level connects omit it.
    mutationFn: async ({ returnTo, variant, projectId } = {}) => {
      const params = new URLSearchParams();
      if (returnTo) params.set('returnTo', returnTo);
      if (variant) params.set('variant', variant);
      if (projectId) params.set('projectId', projectId);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const data = await client.request(`/${service}/oauth/start${qs}`, { method: 'GET' });
      if (!data.authUrl) {
        throw new Error(`Failed to get ${service} OAuth URL`);
      }
      return data;
    },
  });
};

// Discord PER-USER identity binding OAuth start. Separate hook (not
// useStartOAuth) because the route is nested — /discord/user/oauth/start, not
// /<service>/oauth/start — and the backend returns { configured:false, error }
// (no authUrl) when the server-side DISCORD_CLIENT_SECRET / app id aren't wired
// yet. Surfaces that as a clear error so the shared popup opener can toast it.
export const useStartDiscordUserOAuth = () => {
  return useMutation({
    mutationFn: async ({ returnTo } = {}) => {
      const qs = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
      const data = await llmBillingApi.request(`/discord/user/oauth/start${qs}`, { method: 'GET' });
      if (!data.authUrl) {
        throw new Error(data.error || 'Discord identity login is not configured yet.');
      }
      return data;
    },
  });
};

// Discord SERVER/bot INSTALL OAuth start — the ONE-CLICK bot-install flow
// (scope=`bot applications.commands`). Distinct from useStartDiscordUserOAuth
// (per-user `identify`): this installs the shared Zibby bot into the CUSTOMER's
// chosen server and binds that guild to a specific PROJECT. Requires a
// `projectId` (the project the guild is bound to) — passed through as a query
// param on /discord/oauth/start. Like the per-user variant, the backend returns
// { configured:false, error } (no authUrl) when the server-side
// DISCORD_CLIENT_SECRET / app id / redirect URI aren't wired yet.
export const useStartDiscordServerOAuth = () => {
  return useMutation({
    mutationFn: async ({ returnTo, projectId } = {}) => {
      const params = new URLSearchParams();
      if (projectId) params.set('projectId', projectId);
      if (returnTo) params.set('returnTo', returnTo);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const data = await llmBillingApi.request(`/discord/oauth/start${qs}`, { method: 'GET' });
      if (!data.authUrl) {
        throw new Error(data.error || 'Discord one-click install is not configured yet.');
      }
      return data;
    },
  });
};
